import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import documentoService from '../services/documentoService';
import { PERMISOS, PERMISOS_MODULOS } from '../utils/permissions';
import { fechaHoy, fechaHoyMasMeses, parsearFechaLocal } from '../utils/dateUtils';
import '../styles/Graficos.css';
import {
  BarChart, Bar, PieChart, Pie, Cell, Label,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, ComposedChart, Line
} from 'recharts';
import {
  LuCalendarDays, LuChurch, LuUsers, LuPin,
  LuSearch, LuTrash2, LuFileText, LuCircleDollarSign, LuTicketMinus,
  LuChartColumnIncreasing, LuChartColumnDecreasing,
  LuInbox, LuLock, LuFileCheck, LuCircleCheck, LuTriangleAlert ,
  LuTrendingUp, LuChartBar, LuChartPie , LuTrophy, LuBuilding2, LuActivity
} from "react-icons/lu";

const Graficos = () => {
  const { usuario } = useAuth();

  const [filtros, setFiltros] = useState({
    tipoRegistro: 'Todos',
    fechaInicio: fechaHoyMasMeses(-1),
    fechaFin: fechaHoy(),
    estado: '',
    motivo: '',
    tipoDoc: '',
    numDoc: '',
    glosa: '',
    local: usuario?.Local || '',
    grupo: usuario?.GrupoId || '',
    usuario: ''
  });

  const [documentos, setDocumentos] = useState([]);
  const [totales, setTotales] = useState({ ingresos: 0, gastos: 0, saldo: 0 });
  const [catalogos, setCatalogos] = useState({
    usuarios: [], locales: [], grupos: [], motivos: [], tiposDoc: [], estados: []
  });
  const [loading, setLoading] = useState(false);

  const COLORES = {
    ingreso: '#4CAF50',
    gasto: '#F44336',
    saldo: '#2196F3',
    motivos: [
      '#ffca03', '#F3950D', '#CD1818', '#0F2C67',
      '#EEC089', '#D7D0B6', '#8A9DA4', '#4B4D63'
    ]
  };

  const filtrosDeshabilitados = {
    usuario: usuario.Permiso <= PERMISOS.DIGITADOR,
    local: usuario.Permiso < PERMISOS.ZONA,
    grupo: usuario.Permiso < PERMISOS.ADMINISTRADOR
  };

  useEffect(() => {
    cargarCatalogos();
    if (PERMISOS_MODULOS.PUEDE_VER_TODOS_USUARIOS(usuario)) {
      cargarUsuarios();
    }
    consultarDocumentos();
  }, []);

  const cargarCatalogos = async () => {
    try {
      const response = await documentoService.obtenerCatalogos();
      if (response.success) {
        let locales = response.data.locales;
        let grupos = response.data.grupos;
        if (usuario.Permiso < PERMISOS.ZONA) {
          locales = locales.filter(local => local.CodLocal === usuario.Local);
        }
        if (usuario.Permiso < PERMISOS.ADMINISTRADOR) {
          grupos = grupos.filter(grupo => grupo.id === usuario.GrupoId);
        }
        setCatalogos({
          locales, grupos,
          motivos: response.data.motivos,
          tiposDoc: response.data.tiposDoc,
          estados: response.data.estados,
          usuarios: []
        });
      }
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
    }
  };

  const cargarUsuarios = async () => {
    try {
      const response = await documentoService.obtenerUsuarios();
      if (response.success) {
        let usuarios = response.data;
        if (usuario.Permiso <= PERMISOS.DIGITADOR) {
          usuarios = usuarios.filter(u => u.NombreUsu === usuario.NombreUsu);
        } else if (usuario.Permiso === PERMISOS.JEFE_GRUPO) {
          usuarios = usuarios.filter(u => u.GrupoId === usuario.GrupoId);
        } else if (usuario.Permiso >= PERMISOS.ADMINISTRADOR && usuario.Permiso < PERMISOS.ZONA) {
          usuarios = usuarios.filter(u => u.Local === usuario.Local);
        }
        setCatalogos(prev => ({ ...prev, usuarios }));
      }
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
    }
  };

  const consultarDocumentos = async () => {
    setLoading(true);
    try {
      const filtrosParaEnviar = { ...filtros, page: 1, limit: 1000 };
      Object.keys(filtrosParaEnviar).forEach(key => {
        const value = filtrosParaEnviar[key];
        if (value === '' || value === 'Todos' || value === null || value === undefined) {
          delete filtrosParaEnviar[key];
        }
      });
      const [responseDocumentos, responseTotales] = await Promise.all([
        documentoService.obtenerDocumentos(filtrosParaEnviar),
        documentoService.obtenerTotales(filtrosParaEnviar)
      ]);
      if (responseDocumentos.success) {
        setDocumentos(responseDocumentos.data.documentos);
      }
      if (responseTotales.success) {
        setTotales(responseTotales.data);
      }
    } catch (error) {
      console.error('Error al consultar documentos:', error);
      alert('Error al cargar datos de gráficos');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    if (name === 'fechaInicio' && filtros.fechaFin && value > filtros.fechaFin) {
      alert('⚠️ La fecha "Desde" no puede ser mayor que la fecha "Hasta"');
      return;
    }
    if (name === 'fechaFin' && filtros.fechaInicio && value < filtros.fechaInicio) {
      alert('⚠️ La fecha "Hasta" no puede ser menor que la fecha "Desde"');
      return;
    }
    setFiltros(prev => ({ ...prev, [name]: value }));
  };

  const handleTipoRegistroChange = (tipo) => {
    setFiltros(prev => ({ ...prev, tipoRegistro: tipo }));
  };

  const handleConsultar = () => {
    if (filtros.fechaInicio && filtros.fechaFin && filtros.fechaInicio > filtros.fechaFin) {
      alert('⚠️ La fecha "Desde" no puede ser mayor que la fecha "Hasta"');
      return;
    }
    consultarDocumentos();
  };

  const handleLimpiarFiltros = () => {
    setFiltros({
      tipoRegistro: 'Todos',
      fechaInicio: fechaHoyMasMeses(-1),
      fechaFin: fechaHoy(),
      estado: '',
      motivo: '',
      tipoDoc: '',
      numDoc: '',
      glosa: '',
      local: usuario?.Local || '',
      grupo: usuario?.GrupoId || '',
      usuario: ''
    });
  };

  // ========================================
  // PREPARAR DATOS PARA GRÁFICOS
  // ========================================

  // 1. Movimientos diarios (ComposedChart: barras agrupadas + línea saldo)
  const prepararDatosBalance = () => {
    const datosPorFecha = {};
    documentos.forEach(doc => {
      if (doc.Estado !== 'AC') return;
      const fecha = doc.Fecha;
      if (!datosPorFecha[fecha]) {
        datosPorFecha[fecha] = { fecha, ingresos: 0, gastos: 0 };
      }
      if (doc.TipoRegistro === 'Ingreso') {
        datosPorFecha[fecha].ingresos += parseFloat(doc.Monto);
      } else {
        datosPorFecha[fecha].gastos += parseFloat(doc.Monto);
      }
    });
    return Object.values(datosPorFecha)
      .sort((a, b) => a.fecha.localeCompare(b.fecha))
      .map(item => ({
        fecha: parsearFechaLocal(item.fecha).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit' }),
        Ingresos: Math.round(item.ingresos),
        Gastos: Math.round(item.gastos),
        Saldo: Math.round(item.ingresos - item.gastos)
      }));
  };

  // 2. Tendencia acumulada (AreaChart con gradientes)
  const prepararDatosAcumulados = () => {
    const datosPorFecha = {};
    documentos.forEach(doc => {
      if (doc.Estado !== 'AC') return;
      const fecha = doc.Fecha;
      if (!datosPorFecha[fecha]) {
        datosPorFecha[fecha] = { fecha, ingresos: 0, gastos: 0 };
      }
      if (doc.TipoRegistro === 'Ingreso') {
        datosPorFecha[fecha].ingresos += parseFloat(doc.Monto);
      } else {
        datosPorFecha[fecha].gastos += parseFloat(doc.Monto);
      }
    });
    const sorted = Object.values(datosPorFecha)
      .sort((a, b) => a.fecha.localeCompare(b.fecha));
    let acumIngresos = 0, acumGastos = 0;
    return sorted.map(item => {
      acumIngresos += item.ingresos;
      acumGastos += item.gastos;
      return {
        fecha: parsearFechaLocal(item.fecha).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit' }),
        Ingresos: Math.round(acumIngresos),
        Gastos: Math.round(acumGastos),
        Saldo: Math.round(acumIngresos - acumGastos)
      };
    });
  };

  // 3. Distribución por motivo (donuts)
  const prepararDatosMotivos = (tipo) => {
    const datosPorMotivo = {};
    documentos.forEach(doc => {
      if (doc.Estado !== 'AC') return;
      if (tipo && doc.TipoRegistro !== tipo) return;
      const motivo = doc.motivoInfo?.NombreMotivo || doc.Motivo || 'Sin motivo';
      if (!datosPorMotivo[motivo]) datosPorMotivo[motivo] = 0;
      datosPorMotivo[motivo] += parseFloat(doc.Monto);
    });
    return Object.entries(datosPorMotivo)
      .map(([name, value]) => ({ name, value: Math.round(value) }))
      .sort((a, b) => b.value - a.value);
  };

  // 4. Top conceptos combinados (horizontal BarChart)
  const prepararTopMotivos = () => {
    const datosPorMotivo = {};
    documentos.forEach(doc => {
      if (doc.Estado !== 'AC') return;
      const motivo = doc.motivoInfo?.NombreMotivo || doc.Motivo || 'Sin motivo';
      if (!datosPorMotivo[motivo]) {
        datosPorMotivo[motivo] = { name: motivo, Ingresos: 0, Gastos: 0 };
      }
      if (doc.TipoRegistro === 'Ingreso') {
        datosPorMotivo[motivo].Ingresos += parseFloat(doc.Monto);
      } else {
        datosPorMotivo[motivo].Gastos += parseFloat(doc.Monto);
      }
    });
    return Object.values(datosPorMotivo)
      .map(item => ({
        ...item,
        Ingresos: Math.round(item.Ingresos),
        Gastos: Math.round(item.Gastos),
        Total: Math.round(item.Ingresos + item.Gastos)
      }))
      .sort((a, b) => b.Total - a.Total)
      .slice(0, 8);
  };

  // 5. Comparativo por local
  const prepararDatosLocales = () => {
    const datosPorLocal = {};
    documentos.forEach(doc => {
      if (doc.Estado !== 'AC') return;
      const local = doc.localInfo?.Nombre || doc.Local || 'Sin local';
      if (!datosPorLocal[local]) {
        datosPorLocal[local] = { local, Ingresos: 0, Gastos: 0 };
      }
      if (doc.TipoRegistro === 'Ingreso') {
        datosPorLocal[local].Ingresos += parseFloat(doc.Monto);
      } else {
        datosPorLocal[local].Gastos += parseFloat(doc.Monto);
      }
    });
    return Object.values(datosPorLocal).map(item => ({
      local: item.local,
      Ingresos: Math.round(item.Ingresos),
      Gastos: Math.round(item.Gastos)
    }));
  };

  // 6. Estado de documentos (salud financiera)
  const prepararEstadoDocumentos = () => [
    { name: 'Activos', value: documentos.filter(d => d.Estado === 'AC').length, color: '#4CAF50' },
    { name: 'Anulados', value: documentos.filter(d => d.Estado === 'AN').length, color: '#F44336' }
  ];

  const formatMonto = (value) => new Intl.NumberFormat('es-CL', {
    style: 'currency', currency: 'CLP', minimumFractionDigits: 0
  }).format(value || 0);

  const formatMontoCorto = (value) => {
    const abs = Math.abs(value);
    if (abs >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (abs >= 1000) return `$${(value / 1000).toFixed(0)}k`;
    return `$${value}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {formatMonto(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const EmptyState = ({ mensaje = 'Sin datos para el período seleccionado' }) => (
    <div className="empty-state">
      <LuInbox className="empty-icon" />
      <p>{mensaje}</p>
    </div>
  );

  const datosBalance = prepararDatosBalance();
  const datosAcumulados = prepararDatosAcumulados();
  const datosIngresosMotivos = prepararDatosMotivos('Ingreso');
  const datosGastosMotivos = prepararDatosMotivos('Gasto');
  const topMotivos = prepararTopMotivos();
  const datosLocales = prepararDatosLocales();
  const estadoDocumentos = prepararEstadoDocumentos();

  const totalDocs = documentos.length;
  const docsActivos = documentos.filter(d => d.Estado === 'AC').length;
  const docsAnulados = documentos.filter(d => d.Estado === 'AN').length;
  const ratioFinanciero = totales.gastos > 0
    ? (totales.ingresos / totales.gastos).toFixed(2)
    : null;

  return (
    <div className="graficos-container">
      <div className="graficos-header">
        <h2><LuChartBar  /> Análisis de Datos</h2>
        <p className="graficos-subtitle">Visualización interactiva de ingresos y gastos</p>
      </div>

      {/* Filtros */}
      <div className="filtros-card">
        <div className="filtro-section">
          <label className="filtro-label">
            <LuFileText /> Tipo de registro
          </label>
          <div className="tipo-registro-buttons">
            <button
              type="button"
              className={`tipo-btn ${filtros.tipoRegistro === 'Todos' ? 'active todos' : ''}`}
              onClick={() => handleTipoRegistroChange('Todos')}
            >
              Todos
            </button>
            <button
              type="button"
              className={`tipo-btn ${filtros.tipoRegistro === 'Ingreso' ? 'active ingreso' : ''}`}
              onClick={() => handleTipoRegistroChange('Ingreso')}
            >
              Ingreso
            </button>
            <button
              type="button"
              className={`tipo-btn ${filtros.tipoRegistro === 'Gasto' ? 'active gasto' : ''}`}
              onClick={() => handleTipoRegistroChange('Gasto')}
            >
              Gastos
            </button>
          </div>
        </div>

        <div className="filtro-section">
          <label className="filtro-label">
            <LuCalendarDays /> Periodo
          </label>
          <div className="fecha-row">
            <div className="fecha-group">
              <label>Desde</label>
              <input
                type="date"
                name="fechaInicio"
                value={filtros.fechaInicio}
                onChange={handleFiltroChange}
                max={filtros.fechaFin || undefined}
              />
            </div>
            <div className="fecha-group">
              <label>Hasta</label>
              <input
                type="date"
                name="fechaFin"
                value={filtros.fechaFin}
                onChange={handleFiltroChange}
                min={filtros.fechaInicio || undefined}
              />
            </div>
          </div>
        </div>

        <div className="filtros-row">
          <div className="filtro-group">
            <label>
              <LuChurch /> Local
              {filtrosDeshabilitados.local && <span className="badge-bloqueado"><LuLock /></span>}
            </label>
            <select
              name="local"
              value={filtros.local}
              onChange={handleFiltroChange}
              disabled={filtrosDeshabilitados.local}
            >
              <option value="">Todos los locales</option>
              {catalogos.locales.map(local => (
                <option key={local.id} value={local.CodLocal}>{local.Nombre}</option>
              ))}
            </select>
          </div>

          <div className="filtro-group">
            <label>
              <LuUsers /> Grupo
              {filtrosDeshabilitados.grupo && <span className="badge-bloqueado"><LuLock /></span>}
            </label>
            <select
              name="grupo"
              value={filtros.grupo}
              onChange={handleFiltroChange}
              disabled={filtrosDeshabilitados.grupo}
            >
              <option value="">Todos los grupos</option>
              {catalogos.grupos.map(grupo => (
                <option key={grupo.id} value={grupo.id}>{grupo.Descripcion || grupo.Nombre}</option>
              ))}
            </select>
          </div>

          <div className="filtro-group">
            <label><LuPin /> Motivo</label>
            <select name="motivo" value={filtros.motivo} onChange={handleFiltroChange}>
              <option value="">Todos los motivos</option>
              {catalogos.motivos.map(motivo => (
                <option key={motivo.Id} value={motivo.CodMotivo}>{motivo.NombreMotivo}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="filtros-actions">
          <button className="btn-consultar" onClick={handleConsultar} disabled={loading}>
            <LuSearch />
            {loading ? 'Cargando...' : 'Actualizar Gráficos'}
          </button>
          <button className="btn-limpiar" onClick={handleLimpiarFiltros}>
            <LuTrash2 />
            Limpiar Filtros
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="kpis-card">
        <div className="kpi-item ingreso">
          <div className="kpi-icon"><LuCircleDollarSign /></div>
          <div className="kpi-content">
            <span className="kpi-label">Total Ingresos</span>
            <span className="kpi-value">{formatMonto(totales.ingresos)}</span>
          </div>
        </div>
        <div className="kpi-item gasto">
          <div className="kpi-icon"><LuTicketMinus /></div>
          <div className="kpi-content">
            <span className="kpi-label">Total Gastos</span>
            <span className="kpi-value">{formatMonto(totales.gastos)}</span>
          </div>
        </div>
        <div className={`kpi-item ${totales.saldo >= 0 ? 'saldo' : 'gasto'}`}>
          <div className="kpi-icon">{totales.saldo >= 0 ? <LuChartColumnIncreasing /> : <LuChartColumnDecreasing />}</div>
          <div className="kpi-content">
            <span className="kpi-label">Saldo Neto</span>
            <span className="kpi-value">{formatMonto(totales.saldo)}</span>
          </div>
        </div>
        <div className="kpi-item neutral">
          <div className="kpi-icon"><LuFileCheck /></div>
          <div className="kpi-content">
            <span className="kpi-label">Documentos Activos</span>
            <span className="kpi-value">{docsActivos}</span>
            {docsAnulados > 0 && (
              <span className="kpi-sub">{docsAnulados} anulados</span>
            )}
          </div>
        </div>
        {ratioFinanciero !== null && (
          <div className={`kpi-item ${parseFloat(ratioFinanciero) >= 1 ? 'saldo' : 'gasto'}`}>
            <div className="kpi-icon">{parseFloat(ratioFinanciero) >= 1 ? <LuCircleCheck /> : <LuTriangleAlert   />}</div>
            <div className="kpi-content">
              <span className="kpi-label">Ratio Ingreso/Gasto</span>
              <span className="kpi-value">{ratioFinanciero}x</span>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="loading-graficos">
          <div className="spinner"></div>
          <p>Cargando gráficos...</p>
        </div>
      ) : (
        <>
          {/* GRÁFICO 1: Tendencia Acumulada */}
          <div className="grafico-card">
            <div className="grafico-header">
              <h3><LuTrendingUp /> Tendencia Acumulada</h3>
              <p>Evolución acumulada de ingresos, gastos y saldo neto en el período seleccionado</p>
            </div>
            {datosAcumulados.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={datosAcumulados} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradIngresos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#4CAF50" stopOpacity={0.03} />
                    </linearGradient>
                    <linearGradient id="gradGastos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F44336" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#F44336" stopOpacity={0.03} />
                    </linearGradient>
                    <linearGradient id="gradSaldo" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2196F3" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#2196F3" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="fecha" stroke="#888" tick={{ fontSize: 12 }} />
                  <YAxis stroke="#888" tick={{ fontSize: 11 }} tickFormatter={formatMontoCorto} width={72} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area type="monotone" dataKey="Ingresos" stroke="#4CAF50" fill="url(#gradIngresos)" strokeWidth={2.5} />
                  <Area type="monotone" dataKey="Gastos" stroke="#F44336" fill="url(#gradGastos)" strokeWidth={2.5} />
                  <Area type="monotone" dataKey="Saldo" stroke="#2196F3" fill="url(#gradSaldo)" strokeWidth={2} strokeDasharray="6 3" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* GRÁFICO 2: Movimientos por Día */}
          <div className="grafico-card">
            <div className="grafico-header">
              <h3><LuChartBar  /> Movimientos por Día</h3>
              <p>Detalle diario de ingresos y gastos con saldo neto del período</p>
            </div>
            {datosBalance.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={datosBalance} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="fecha" stroke="#888" tick={{ fontSize: 12 }} />
                  <YAxis stroke="#888" tick={{ fontSize: 11 }} tickFormatter={formatMontoCorto} width={72} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="Ingresos" fill="#4CAF50" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  <Bar dataKey="Gastos" fill="#F44336" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  <Line
                    type="monotone"
                    dataKey="Saldo"
                    stroke="#2196F3"
                    strokeWidth={2.5}
                    dot={{ fill: '#2196F3', r: 4, strokeWidth: 2, stroke: 'white' }}
                    activeDot={{ r: 6 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* GRÁFICOS 3 y 4: Donuts por Motivo */}
          <div className="graficos-row">
            <div className="grafico-card">
              <div className="grafico-header">
                <h3><LuChartPie   className="icon-ingreso" /> Ingresos por Concepto</h3>
                <p>Distribución de ingresos activos según motivo</p>
              </div>
              {datosIngresosMotivos.length === 0 ? <EmptyState /> : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={datosIngresosMotivos}
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={105}
                      dataKey="value"
                      paddingAngle={2}
                    >
                      {datosIngresosMotivos.map((_, index) => (
                        <Cell key={`cell-i-${index}`} fill={COLORES.motivos[index % COLORES.motivos.length]} />
                      ))}
                      <Label
                        content={({ viewBox }) => {
                          const { cx, cy } = viewBox;
                          return (
                            <text x={cx} y={cy} textAnchor="middle">
                              <tspan x={cx} dy="-10" fontSize="11" fill="#888">Total</tspan>
                              <tspan x={cx} dy="22" fontSize="14" fontWeight="700" fill="#4CAF50">
                                {formatMontoCorto(totales.ingresos)}
                              </tspan>
                            </text>
                          );
                        }}
                        position="right"
                      />
                    </Pie>
                    <Tooltip formatter={(value) => formatMonto(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="grafico-card">
              <div className="grafico-header">
                <h3><LuChartPie   className="icon-gasto" /> Gastos por Concepto</h3>
                <p>Distribución de gastos activos según motivo</p>
              </div>
              {datosGastosMotivos.length === 0 ? <EmptyState /> : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={datosGastosMotivos}
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={105}
                      dataKey="value"
                      paddingAngle={2}
                    >
                      {datosGastosMotivos.map((_, index) => (
                        <Cell key={`cell-g-${index}`} fill={COLORES.motivos[index % COLORES.motivos.length]} />
                      ))}
                      <Label
                        content={({ viewBox }) => {
                          const { cx, cy } = viewBox;
                          return (
                            <text x={cx} y={cy} textAnchor="middle">
                              <tspan x={cx} dy="-10" fontSize="11" fill="#888">Total</tspan>
                              <tspan x={cx} dy="22" fontSize="14" fontWeight="700" fill="#F44336">
                                {formatMontoCorto(totales.gastos)}
                              </tspan>
                            </text>
                          );
                        }}
                        position="right"
                      />
                    </Pie>
                    <Tooltip formatter={(value) => formatMonto(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* GRÁFICO 5: Top Conceptos — Horizontal BarChart */}
          <div className="grafico-card">
            <div className="grafico-header">
              <h3><LuTrophy /> Top Conceptos por Volumen</h3>
              <p>Principales motivos de transacción ordenados por impacto financiero total</p>
            </div>
            {topMotivos.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height={Math.max(280, topMotivos.length * 56)}>
                <BarChart
                  layout="vertical"
                  data={topMotivos}
                  margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis
                    type="number"
                    stroke="#888"
                    tick={{ fontSize: 11 }}
                    tickFormatter={formatMontoCorto}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={155}
                    stroke="#888"
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="Ingresos" fill="#4CAF50" radius={[0, 4, 4, 0]} maxBarSize={22} />
                  <Bar dataKey="Gastos" fill="#F44336" radius={[0, 4, 4, 0]} maxBarSize={22} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* GRÁFICOS 6 y 7: Local y Salud Financiera */}
          <div className="graficos-row">
            <div className="grafico-card">
              <div className="grafico-header">
                <h3><LuBuilding2 /> Comparativo por Local</h3>
                <p>Ingresos vs Gastos por sede o congregación</p>
              </div>
              {datosLocales.length === 0 ? <EmptyState /> : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={datosLocales} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="local" stroke="#888" tick={{ fontSize: 12 }} />
                    <YAxis stroke="#888" tick={{ fontSize: 11 }} tickFormatter={formatMontoCorto} width={65} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="Ingresos" fill="#4CAF50" radius={[8, 8, 0, 0]} maxBarSize={50} />
                    <Bar dataKey="Gastos" fill="#F44336" radius={[8, 8, 0, 0]} maxBarSize={50} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Salud Financiera */}
            <div className="grafico-card">
              <div className="grafico-header">
                <h3><LuActivity /> Salud Financiera</h3>
                <p>Indicadores clave del período analizado</p>
              </div>
              <div className="salud-container">
                <div className="salud-item">
                  <div className="salud-item-header">
                    <span className="salud-label">Ratio Ingreso / Gasto</span>
                    <span className={`salud-badge ${ratioFinanciero && parseFloat(ratioFinanciero) >= 1 ? 'positivo' : 'negativo'}`}>
                      {ratioFinanciero ? `${ratioFinanciero}x` : '—'}
                    </span>
                  </div>
                  <div className="salud-progress">
                    <div
                      className="salud-progress-bar"
                      style={{
                        width: `${ratioFinanciero ? Math.min(100, parseFloat(ratioFinanciero) * 50) : 0}%`,
                        background: ratioFinanciero && parseFloat(ratioFinanciero) >= 1 ? '#4CAF50' : '#F44336'
                      }}
                    />
                  </div>
                  <p className="salud-desc">
                    {!ratioFinanciero
                      ? 'Sin gastos registrados en el período'
                      : parseFloat(ratioFinanciero) >= 1
                        ? `Los ingresos superan los gastos en ${formatMontoCorto(totales.saldo)}`
                        : `Los gastos superan los ingresos en ${formatMontoCorto(Math.abs(totales.saldo))}`}
                  </p>
                </div>

                <div className="salud-divider" />

                <div className="salud-item">
                  <div className="salud-item-header">
                    <span className="salud-label">Validez de Documentos</span>
                    <span className={`salud-badge ${docsAnulados === 0 ? 'positivo' : docsAnulados / totalDocs < 0.1 ? 'alerta' : 'negativo'}`}>
                      {totalDocs > 0 ? `${((docsActivos / totalDocs) * 100).toFixed(0)}% válidos` : '—'}
                    </span>
                  </div>
                  <div className="salud-progress">
                    <div
                      className="salud-progress-bar"
                      style={{
                        width: `${totalDocs > 0 ? (docsActivos / totalDocs) * 100 : 0}%`,
                        background: docsAnulados === 0 ? '#4CAF50' : docsAnulados / totalDocs < 0.1 ? '#FF9800' : '#F44336'
                      }}
                    />
                  </div>
                  <p className="salud-desc">
                    {docsActivos} activos · {docsAnulados} anulados · {totalDocs} total
                  </p>
                </div>

                {totalDocs > 0 && docsAnulados > 0 && (
                  <>
                    <div className="salud-divider" />
                    <ResponsiveContainer width="100%" height={160}>
                      <PieChart>
                        <Pie
                          data={estadoDocumentos}
                          cx="50%"
                          cy="50%"
                          innerRadius={42}
                          outerRadius={62}
                          dataKey="value"
                          paddingAngle={3}
                        >
                          {estadoDocumentos.map(({ color }, index) => (
                            <Cell key={`cell-e-${index}`} fill={color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [value, name]} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Graficos;
